import React from 'react';
import './GmailPreviewPanel.css';

/**
 * Gmail organizer UI for the shared Preview panel stage.
 */
export default function GmailPreviewPanel({
  GmailLogo,
  gmailConnected,
  gmailLoading,
  gmailOrganizing,
  gmailProgress,
  gmailError,
  gmailMessages = [],
  gmailLastRefresh,
  onConnect,
  onRefresh,
  onOrganize,
  classifyMessage,
  getLabelSummary,
}) {
  const summary = typeof getLabelSummary === 'function' ? getLabelSummary(gmailMessages) : {};

  return (
    <div className="gmail-preview" role="region" aria-label="Gmail in Preview panel">
      <div className="gmail-preview-toolbar">
        <div className="gmail-preview-brand">
          {GmailLogo ? <GmailLogo /> : null}
          <span>Gmail</span>
        </div>
        <div className={`gmail-preview-status ${gmailConnected ? 'ok' : 'warn'}`}>
          {gmailConnected ? 'Connected' : 'Not connected'}
        </div>
      </div>

      <div className="gmail-preview-actions">
        <button type="button" className="gmail-preview-btn" onClick={onConnect} disabled={gmailConnected}>
          {gmailConnected ? 'Connected' : 'Connect Gmail'}
        </button>
        <button
          type="button"
          className="gmail-preview-btn"
          onClick={() => window.open('https://mail.google.com/', '_blank', 'noopener,noreferrer')}
        >
          Open Gmail
        </button>
        <button type="button" className="gmail-preview-btn" onClick={onRefresh} disabled={gmailLoading}>
          {gmailLoading ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="gmail-preview-btn primary"
          onClick={onOrganize}
          disabled={gmailLoading || gmailOrganizing}
        >
          Organize (up to 7 labels)
        </button>
      </div>

      <div className="gmail-preview-hint">
        {gmailConnected
          ? (gmailLastRefresh ? `Last refreshed: ${new Date(gmailLastRefresh).toLocaleString()}` : 'Not refreshed yet')
          : 'Connect Gmail to load your recent emails.'}
      </div>

      {gmailMessages?.length > 0 && (
        <div className="gmail-preview-summary">
          {Object.entries(summary).map(([label, count]) => (
            <div key={label} className="gmail-preview-chip">
              <span className="gmail-preview-chip-name">{label}</span>
              <span className="gmail-preview-chip-count">{count}</span>
            </div>
          ))}
        </div>
      )}

      {!!gmailError && <div className="gmail-preview-error">{gmailError}</div>}

      {gmailOrganizing && (
        <div className="gmail-preview-progress">
          Labeling… {gmailProgress?.done || 0}/{gmailProgress?.total || 0}
          <div className="gmail-preview-progressbar" aria-hidden="true">
            <div
              className="gmail-preview-progressbar-fill"
              style={{
                width: gmailProgress?.total
                  ? `${Math.min(100, Math.round((gmailProgress.done / gmailProgress.total) * 100))}%`
                  : '0%',
              }}
            />
          </div>
        </div>
      )}

      <div className="gmail-preview-list">
        {gmailLoading && <div className="gmail-preview-loading">Loading emails…</div>}
        {(gmailMessages || []).slice(0, 20).map((m) => (
          <div key={m.id} className="gmail-preview-item">
            <div className="gmail-preview-item-top">
              <div className="gmail-preview-subject">{m.subject || '(No subject)'}</div>
              <div className="gmail-preview-label">
                {typeof classifyMessage === 'function' ? classifyMessage(m) : ''}
              </div>
            </div>
            <div className="gmail-preview-from">{m.from}</div>
            {!!m.snippet && <div className="gmail-preview-snippet">{m.snippet}</div>}
          </div>
        ))}
        {!gmailLoading && (!gmailMessages || gmailMessages.length === 0) && (
          <div className="gmail-preview-empty">
            {gmailConnected ? 'No messages loaded yet. Click “Refresh”.' : 'Not connected yet. Click “Connect Gmail”.'}
          </div>
        )}
      </div>
    </div>
  );
}
